import { useEffect, useState } from 'react'
import { Pagination } from '../../../common/components/Pagination'
import { BackofficePermissionsEnum as Permissions } from '../../../common/enums/backOfficePermissionsEnum'
import {
  SpectrumBankAccountOperation,
  SpectrumIdentityTypeEnum,
} from '../../../common/enums/spectrumBankAccountEnum'
import { useDebounce } from '../../../common/hooks/useDebounce'
import usePermissions from '../../../common/hooks/usePermissions'
import { customSwal } from '../../../config/swal'
import { SpectrumBankAccountService } from '../../../services/SpectrumBankAccountService'
import { SpectrumBankAccountFiltersDrawer } from './components/SpectrumBankAccountFiltersDrawer'
import { SpectrumBankAccountTable } from './components/SpectrumBankAccountTable'
import {
  initialSpectrumBankAccountFilters,
  ISpectrumBankAccountFilters,
  useSpectrumBankAccounts,
} from './hooks/useSpectrumBankAccounts'

const PAGE_SIZE = 10

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')

export const SpectrumBankAccounts = () => {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 500)
  const [drawerFilters, setDrawerFilters] = useState(initialSpectrumBankAccountFilters)
  const [page, setPage] = useState(1)
  const [isExporting, setIsExporting] = useState(false)
  const { hasPermission } = usePermissions()

  const numIdentidad = drawerFilters.numIdentidad || debouncedSearch || ''

  const filters: ISpectrumBankAccountFilters = {
    ...drawerFilters,
    tipoIdentidad:
      drawerFilters.tipoIdentidad || (numIdentidad ? SpectrumIdentityTypeEnum.DNI : ''),
    numIdentidad,
  }

  const {
    query: { data, isLoading, isFetching, isError, error, refetch },
    selected,
    toggleSelected,
    clearSelected,
    approveRejectSelected,
    isBulkActionLoading,
    canOperate,
  } = useSpectrumBankAccounts(filters, page, PAGE_SIZE)

  const filtersKey = JSON.stringify(filters)
  useEffect(() => {
    setPage(1)
  }, [filtersKey])

  const totalPages = data ? Math.max(Math.ceil(data.total / data.limit), 1) : 1
  const isInitialLoad = isLoading && !data
  const isRefreshing = isFetching && !isInitialLoad

  const confirmBulkAction = (operation: SpectrumBankAccountOperation) => {
    const actionLabel = operation === SpectrumBankAccountOperation.APPROVE ? 'Aprobado' : 'Anulado'

    customSwal
      .fire({
        title: `¿Está seguro que desea cambiar el estado de la cuenta a <strong>${actionLabel}</strong>?`,
        icon: 'warning',
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: `Sí, cambiar`,
        cancelButtonText: 'Cancelar',
      })
      .then((result) => {
        if (!result.isConfirmed) return

        approveRejectSelected(operation).then((outcome) => {
          if (!outcome) return

          if (outcome.ok) {
            customSwal.fire({ icon: 'success', title: 'Cuenta actualizada correctamente.' })
            return
          }

          customSwal.fire({
            icon: 'error',
            title: 'No se pudo procesar la cuenta',
            html: `${escapeHtml(outcome.message || 'Error al procesar la cuenta.')}`,
          })
        })
      })
  }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const blob = await SpectrumBankAccountService().exportAccounts()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'cuentas-bancarias-spectrum.xlsx'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch {
      customSwal.fire({ title: 'Error al exportar las cuentas bancarias', icon: 'error' })
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className='card border-0 pt-5 border-radius-20px'>
      <div className='m-5 d-flex flex-wrap align-items-end justify-content-end gap-5'>
        <div className='d-flex align-items-center gap-3'>
          {' '}
          {JSON.stringify(drawerFilters) !== JSON.stringify(initialSpectrumBankAccountFilters) && (
            <span
              style={{ borderBottom: '1px inset #a1a5b7' }}
              className='text-muted cursor-pointer d-block fs-6'
              onClick={() => setDrawerFilters(initialSpectrumBankAccountFilters)}
            >
              Limpiar filtros
            </span>
          )}
          <div
            style={{ background: 'var(--color-primary-blue)', height: '42px', width: '42px' }}
            className='rounded p-2 d-flex justify-content-center align-items-center cursor-pointer'
            data-bs-toggle='modal'
            data-bs-target='#modal-spectrum-bank-account-filters'
          >
            <i className='bi bi-filter fs-2 text-white'></i>
          </div>
        </div>
        <div>
          {' '}
          {hasPermission(Permissions.ACCOUNT_VALIDATED.EXPORT) && (
            <button
              style={{
                backgroundColor: '#007BC3',
                color: 'white',
                width: isExporting ? '140px' : '120px',
                height: '42px',
              }}
              onClick={handleExport}
              className='btn text-end cursor-pointer'
            >
              {isExporting ? (
                <span className='spinner-border spinner-border-sm me-2' role='status'></span>
              ) : (
                <i className='bi bi-download me-2 text-white'></i>
              )}
              {isExporting ? 'Exportando' : 'Exportar'}
            </button>
          )}
        </div>
      </div>

      {selected && (
        <div
          style={{ border: '1px solid #eee' }}
          className='card-body rounded position-relative d-flex align-items-center justify-content-between w-300px mx-9 py-2'
        >
          <div className='d-flex gap-3'>
            {hasPermission(Permissions.ACCOUNT_VALIDATED.APPROVE) && (
              <button
                type='button'
                disabled={!canOperate || isBulkActionLoading}
                title={canOperate ? undefined : 'Solo se pueden aprobar cuentas pendientes'}
                style={{ border: 'none' }}
                onClick={() => confirmBulkAction(SpectrumBankAccountOperation.APPROVE)}
                className='flex-grow-1 badge-light badge rounded py-2 px-3'
              >
                Aprobar
              </button>
            )}
            {hasPermission(Permissions.ACCOUNT_VALIDATED.CANCEL) && (
              <button
                type='button'
                disabled={!canOperate || isBulkActionLoading}
                title={canOperate ? undefined : 'Solo se pueden anular cuentas pendientes'}
                style={{ border: 'none' }}
                onClick={() => confirmBulkAction(SpectrumBankAccountOperation.REJECT)}
                className='flex-grow-1 badge-light badge rounded py-2 px-3'
              >
                Anular
              </button>
            )}
          </div>
          <div
            onClick={clearSelected}
            style={{
              borderBottom: '1px inset #a1a5b7',
              position: 'absolute',
              right: '-130px',
            }}
            className='text-muted cursor-pointer'
          >
            Limpiar selección
          </div>
        </div>
      )}

      <div className='card-body pt-0'>
        {isError && (
          <div className='alert alert-danger d-flex align-items-center justify-content-between mb-4'>
            <span>
              Ocurrió un error al buscar las cuentas bancarias
              {error?.response?.status ? ` (${error.response.status})` : ''}.
            </span>
            <button type='button' className='btn btn-sm btn-light-danger' onClick={() => refetch()}>
              Reintentar
            </button>
          </div>
        )}
        <div className='table-responsive mb-4 position-relative'>
          {isInitialLoad ? (
            <div className='text-center m-5 p-10'>
              <div className='spinner-border' role='status'></div>
            </div>
          ) : isError && !data ? null : (
            <>
              <div
                style={{
                  opacity: isRefreshing ? 0.4 : 1,
                  transition: 'opacity 200ms ease-out',
                }}
              >
                <SpectrumBankAccountTable
                  rows={data?.data ?? []}
                  filters={filters}
                  selected={selected}
                  onToggleSelected={toggleSelected}
                />
              </div>

              {isRefreshing && (
                <div
                  className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
                  style={{ pointerEvents: 'none' }}
                >
                  <div className='spinner-border spinner-border-sm' role='status'></div>
                </div>
              )}
            </>
          )}

          {!isInitialLoad && !isRefreshing && !isError && data?.data.length === 0 && (
            <span className='notFound text-muted fs-5 text-center mt-8 mb-8 d-block'>
              No se encontraron resultados
            </span>
          )}
        </div>
        <div className='d-flex align-items-end justify-content-end'>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>{' '}
      </div>

      <SpectrumBankAccountFiltersDrawer filters={drawerFilters} setFilters={setDrawerFilters} />
    </div>
  )
}
